import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";

/**
 * BankDetailsModal — affiche IBAN + comm structurée pour une facture donnée.
 * Design sobre, adapté au hub client (fond clair, couleur d'accent entreprise).
 */
export default function BankDetailsModal({
  open,
  onOpenChange,
  companyName,
  ibanFormatted,
  ibanRaw,
  structuredComm,
  accent = "#C4553B",
}) {
  const [copied, setCopied] = useState({ iban: false, comm: false });

  const copy = async (which, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied((c) => ({ ...c, [which]: true }));
      setTimeout(() => setCopied((c) => ({ ...c, [which]: false })), 2000);
    } catch (_) {
      // Fallback silencieux — l'utilisateur peut sélectionner à la main
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md border border-[#E5DDD1] bg-white text-[#2A2420]"
        data-testid="bank-details-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-[#2A2420]">Coordonnées bancaires</DialogTitle>
          <DialogDescription className="text-[#5B5148]">
            Effectuez le virement en utilisant précisément ces informations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded border border-[#E5DDD1] bg-[#F7F3EC] p-3">
            <div className="text-xs uppercase tracking-wide text-[#5B5148]">Bénéficiaire</div>
            <div className="mt-1 text-base font-semibold" data-testid="bank-beneficiary">
              {companyName}
            </div>
          </div>

          <div className="rounded border border-[#E5DDD1] bg-[#F7F3EC] p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-[#5B5148]">IBAN</div>
                <div
                  className="mt-1 font-mono text-base tracking-wide"
                  data-testid="bank-iban"
                >
                  {ibanFormatted || "—"}
                </div>
              </div>
              {ibanRaw && (
                <Button
                  onClick={() => copy("iban", ibanRaw)}
                  variant="outline"
                  size="sm"
                  className="border-[#D9CFC1] bg-white text-[#2A2420] hover:bg-[#F0EAE0]"
                  data-testid="bank-iban-copy"
                >
                  {copied.iban ? (
                    <><Check className="mr-1 h-3.5 w-3.5" /> Copié</>
                  ) : (
                    <><Copy className="mr-1 h-3.5 w-3.5" /> Copier</>
                  )}
                </Button>
              )}
            </div>
          </div>

          {structuredComm && (
            <div className="rounded border border-[#E5DDD1] bg-[#F7F3EC] p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wide text-[#5B5148]">
                    Communication structurée
                  </div>
                  <div
                    className="mt-1 font-mono text-base tracking-wide"
                    style={{ color: accent }}
                    data-testid="bank-comm"
                  >
                    {structuredComm}
                  </div>
                </div>
                <Button
                  onClick={() => copy("comm", structuredComm)}
                  variant="outline"
                  size="sm"
                  className="border-[#D9CFC1] bg-white text-[#2A2420] hover:bg-[#F0EAE0]"
                  data-testid="bank-comm-copy"
                >
                  {copied.comm ? (
                    <><Check className="mr-1 h-3.5 w-3.5" /> Copié</>
                  ) : (
                    <><Copy className="mr-1 h-3.5 w-3.5" /> Copier</>
                  )}
                </Button>
              </div>
              <p className="mt-2 text-xs text-[#5B5148]">
                Indispensable pour rattacher votre paiement à la bonne facture.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
